/**
 * #1242 — "What Kin's learned" stays scannable as it grows.
 *
 * Kasandra chose the per-group cap plus a filter box (2026-09-12). The screen's
 * job is that she can see and correct what Kin believes, so the rules here are
 * all about never hiding something she can't get back to:
 *
 *   - Each group shows its first LEARNED_GROUP_CAP facts, then "Show all N".
 *     A group is never collapsed to nothing, and the count says what's hidden.
 *   - Unconfirmed chat guesses sort FIRST. They are the facts most likely to be
 *     wrong, so they must be the last thing a cap can push out of sight.
 *   - Filtering shows EVERY match, uncapped. A search that hid matches behind
 *     "Show all" would be a search that lies.
 *   - A fact she is editing, just added, or was deep-linked to is always shown,
 *     even if it would otherwise sit below the cap.
 *
 * Pure, so the rules are tested directly rather than through the 1,800-line
 * component that renders them.
 */

export const LEARNED_GROUP_CAP = 5;

/** The filter box appears once there is more than one group's cap of facts
 *  overall. Below that the whole list fits on a screen and a box is clutter. */
export const LEARNED_FILTER_MIN_FACTS = LEARNED_GROUP_CAP + 1;

export interface ViewFact {
  id: string;
}

export interface ViewGroup<F extends ViewFact> {
  id: string;
  title: string;
  facts: F[];
  emptyCopy?: string;
}

export interface GroupView<F extends ViewFact, G extends ViewGroup<F>> {
  group: G;
  /** Facts to render, in display order. */
  shown: F[];
  /** Facts below the cap. 0 when expanded or filtering. */
  hiddenCount: number;
  /** Total facts in the group (after filtering, when a filter is active). */
  total: number;
  /** True when the group shows everything because she (or a pinned fact) opened it. */
  expanded: boolean;
}

export interface LearnedViewOptions<F extends ViewFact> {
  query: string;
  expandedGroupIds: ReadonlySet<string>;
  /** Groups she collapsed with "Show fewer". Overrides soft pins, never hard ones. */
  collapsedGroupIds?: ReadonlySet<string>;
  /** Facts that must be visible whatever the cap says — the one she's EDITING.
   *  Collapsing can't hide these: an open editor below the cap would vanish. */
  hardPinnedFactIds?: ReadonlySet<string>;
  /** Facts shown on arrival (just added, deep link). Visible until she collapses the group. */
  pinnedFactIds: ReadonlySet<string>;
  isGuess: (fact: F) => boolean;
  /** Everything a person would read on the row, lowercased by the caller or not — matching is case-insensitive. */
  searchText: (fact: F) => string;
}

/** Guesses first; otherwise the order the groups memo already chose. */
export function orderForReview<F extends ViewFact>(facts: F[], isGuess: (f: F) => boolean): F[] {
  const guesses: F[] = [];
  const rest: F[] = [];
  for (const f of facts) (isGuess(f) ? guesses : rest).push(f);
  return [...guesses, ...rest];
}

export function normalizeQuery(query: string): string {
  return query.trim().toLowerCase().replace(/\s+/g, " ");
}

export function buildLearnedView<F extends ViewFact, G extends ViewGroup<F>>(
  groups: G[],
  opts: LearnedViewOptions<F>,
): GroupView<F, G>[] {
  const q = normalizeQuery(opts.query);
  const out: GroupView<F, G>[] = [];

  for (const group of groups) {
    const ordered = orderForReview(group.facts, opts.isGuess);

    if (q) {
      // A group whose NAME matches ("Caleb") shows all of its facts; otherwise
      // only the facts that match. Empty-invitation groups have nothing to find.
      // Both sides normalised the same way, so "Dr  Lee" in a stored value
      // matches a search for "dr lee" (Copilot on #1614).
      const titleHit = normalizeQuery(group.title).includes(q);
      const matches = titleHit ? ordered : ordered.filter((f) => normalizeQuery(opts.searchText(f)).includes(q));
      if (matches.length === 0) continue;
      out.push({ group, shown: matches, hiddenCount: 0, total: matches.length, expanded: true });
      continue;
    }

    const belowCap = ordered.slice(LEARNED_GROUP_CAP);
    const hardPinned = belowCap.some((f) => opts.hardPinnedFactIds?.has(f.id));
    const softPinned = belowCap.some((f) => opts.pinnedFactIds.has(f.id)) && !opts.collapsedGroupIds?.has(group.id);
    const expanded = opts.expandedGroupIds.has(group.id) || hardPinned || softPinned;
    const shown = expanded ? ordered : ordered.slice(0, LEARNED_GROUP_CAP);
    out.push({
      group,
      shown,
      hiddenCount: ordered.length - shown.length,
      total: ordered.length,
      expanded,
    });
  }
  return out;
}

export function countFacts<F extends ViewFact>(groups: ViewGroup<F>[]): number {
  return groups.reduce((n, g) => n + g.facts.length, 0);
}
